<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useMeStore } from '~/stores/me';
import { useLabStore } from '~/stores/lab';
import { useCountryStore } from '~/stores/countries';
import $fetch from '~/modules/fetch';
import MapViewer from '~/components/common/MapViewer.vue';

import { Lab } from '~/models/labs';
import { countryIsoToCountryName } from '~/composables/countries';

// PROPS
interface Props {
  lab: any;
}

const props = withDefaults(defineProps<Props>(), {
  lab: {},
});

// STORES
const labStore = useLabStore();
const me = useMeStore();
const countries = useCountryStore();

// VARIABLES
const postalAddress = ref();
const map = ref();

// COMPUTED
const city = computed(() => (postalAddress.value ? postalAddress.value.city : props.lab.city));
const country = computed(() =>
  countryIsoToCountryName(props.lab.country.name || props.lab.country),
);

const labs = computed(() => {
  // Make coordinates compatible
  if (!props.lab) {
    return [];
  }

  if (props.lab.latitude && props.lab.longitude) {
    return [props.lab];
  }

  const { lab } = labStore;
  return [
    {
      ...lab,
      latitude: lab?.geolocLatitude,
      longitude: lab?.geolocLongitude,
    },
  ];
});
const labelText = computed(() => `${city.value}, ${country.value}`);
const isLoggedIn = computed(() => me.isLoggedIn);
const labMarker: any = computed(() => {
  if (map.value.markersList) {
    return Object.values(map.value.markersList)[0];
  }

  return null;
});

const fetchPostalAddress = async () => {
  const postalAddressFromLab = props.lab['postal-address'].split('/').pop();
  return $fetch.get(`/resources/mdpeoplecompany/postal-addresses/${postalAddressFromLab}`);
};

const buildMapView = async () => {
  const { geolocLatitude, geolocLongitude } = labStore.lab as Lab;
  await map.value.loadMap();
  await map.value.updateMap();
  await map.value.setMapPosition([geolocLatitude, geolocLongitude], 10);

  if (labMarker.value) {
    labMarker.value.openPopup();
  }
};

// MOUNTED
onMounted(async () => {
  await countries.fetchCountries();

  if (isLoggedIn.value && !props.lab.city) {
    postalAddress.value = await fetchPostalAddress();
  }

  buildMapView();
});
</script>

<template>
  <MapViewer
    ref="map"
    class="showroom-container my-10px"
    select-label="Selection label"
    :explore="true"
    :labs-list="labs"
    :selected-labs="[]"
    :custom-label="labelText"
    :zoom-with-control-and-wheel="true"
  />
</template>

<style land="scss">
.labs-map-container {
  width: 100%;
  height: 500px;
  background-color: #ffffff;
  display: inline-block;
}
.labs-map-container .unusable {
  pointer-events: none;
}
.labs-map-container .mp3dp-map {
  height: 100%;
  width: 100%;
}
.labs-map-container .mp3dp-map .map-container {
  height: 100%;
  width: 100%;
}
.labs-map-container .mp3dp-map .map-container .leaflet-popup-content-wrapper {
  border-radius: 5px;
}
.labs-map-container
  .mp3dp-map
  .map-container
  .leaflet-popup-content-wrapper
  .leaflet-popup-content {
  display: inline-block;
  margin: 3px 5px;
  padding: 10px 5px;
  width: 100%;
  min-width: 230px;
  height: 110px;
}
.labs-map-container
  .mp3dp-map
  .map-container
  .leaflet-popup-content-wrapper
  .leaflet-popup-content
  .lab-title {
  text-transform: capitalize;
  font-size: 18px;
  font-weight: bold;
  margin: 0px;
  padding: 0px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  color: #3d3d3d;
}
.labs-map-container
  .mp3dp-map
  .map-container
  .leaflet-popup-content-wrapper
  .leaflet-popup-content
  .lab-card-container {
  width: 100%;
  padding: 5px 0 0 0;
  display: flex;
  flex-direction: row;
  border: none;
}
.labs-map-container
  .mp3dp-map
  .map-container
  .leaflet-popup-content-wrapper
  .leaflet-popup-content
  .lab-initial-container {
  flex-shrink: 0;
}
.labs-map-container
  .mp3dp-map
  .map-container
  .leaflet-popup-content-wrapper
  .leaflet-popup-content
  .lab-template-container {
  flex: 1 1 auto;
}
.labs-map-container
  .mp3dp-map
  .map-container
  .leaflet-popup-content-wrapper
  .leaflet-popup-content
  .price-div {
  height: 40px;
  line-height: 40px;
  padding: 0;
}
.labs-map-container
  .mp3dp-map
  .map-container
  .leaflet-popup-content-wrapper
  .leaflet-popup-content
  .price-div-extended {
  height: 50px;
  line-height: 50px;
  padding: 0;
}
.labs-map-container
  .mp3dp-map
  .map-container
  .leaflet-popup-content-wrapper
  .leaflet-popup-content
  .more-information-lab-select {
  line-height: 20px;
  font-size: 14px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  display: block;
  padding: 0;
}
.labs-map-container
  .mp3dp-map
  .map-container
  .leaflet-popup-content-wrapper
  .leaflet-popup-content
  .stars-container {
  height: 20px;
  padding: 0px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}
.labs-map-container
  .mp3dp-map
  .map-container
  .leaflet-popup-content-wrapper
  .leaflet-popup-content
  .starting {
  height: 10px;
  line-height: 10px;
  padding: 0;
  font-size: 10px;
  font-weight: bold;
  color: #3d3d3d;
}
.labs-map-container
  .mp3dp-map
  .map-container
  .leaflet-popup-content-wrapper
  .leaflet-popup-content
  .more-information-lab-select {
  line-height: 20px;
  font-size: 14px;
}
.labs-map-container
  .mp3dp-map
  .map-container
  .leaflet-popup-content-wrapper
  .leaflet-popup-content
  .collection-search-lab-button-select {
  width: 100%;
  height: 35px;
  display: block;
}
.labs-map-container
  .mp3dp-map
  .map-container
  .leaflet-popup-content-wrapper
  .leaflet-popup-content
  .dot-container {
  padding: 0px 0px 2px;
}
.labs-map-container
  .mp3dp-map
  .map-container
  .leaflet-popup-content-wrapper
  .leaflet-popup-content
  .logo-container {
  padding: 0;
}
.labs-map-container
  .mp3dp-map
  .map-container
  .leaflet-popup-content-wrapper
  .leaflet-popup-content
  .txt-container {
  display: none;
  padding: 0;
}
</style>
